import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import type { DtoV1Borrower1003GetBorrowerSummaryDataResponse } from '~/__generated__/api';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import useUrl, { type ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';

import { useBorrower1003Store } from './borrower1003Store';

type DeleteBorrowerGridRow = {
  customerRecordId?: number | null;
  lenderdatabaseId?: number | null;
};

function transformBorrowerData(
  data: DtoV1Borrower1003GetBorrowerSummaryDataResponse,
) {
  const borrowers = (data.borrowers ?? []).toSorted(
    (a, b) => (a.borrowerPosition ?? 0) - (b.borrowerPosition ?? 0),
  );

  return {
    ...data,
    borrowers,
    isDeleteRowDisabled: borrowers.length === 1,
  };
}

export type BorrowerData = ReturnType<typeof transformBorrowerData>;

export const borrower1003QueryKeys = {
  borrowerData: (obfuscatedLoanIdentifier: ObfuscatedLoanIdentifier) => [
    '1003BorrowerData',
    obfuscatedLoanIdentifier,
  ],
};

export function use1003BorrowerData<TSelected = BorrowerData>({
  select,
}: {
  select?: (data: BorrowerData) => TSelected;
} = {}) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: borrower1003QueryKeys.borrowerData(obfuscatedLoanIdentifier),
    staleTime: 60,
    refetchOnMount: false,
    queryFn: async () => {
      const response =
        await typedApiClient.v1.borrower1003ValidationBorrowersList(
          obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
          obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        );

      return transformBorrowerData(response.data);
    },
    select,
  });
}

function getBorrowerIdentifiersForDelete(rows: DeleteBorrowerGridRow[]) {
  const borrower = rows[0];
  const borrowerRecordId = borrower?.customerRecordId;
  const borrowerLenderDatabaseId = borrower?.lenderdatabaseId;

  if (
    typeof borrowerRecordId !== 'number' ||
    typeof borrowerLenderDatabaseId !== 'number'
  ) {
    throw new TypeError('Borrower identifier is required for deletion.');
  }

  return {
    borrowerRecordId,
    borrowerLenderDatabaseId,
  };
}

export function useDeleteBorrower1003Mutation() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (rows: DeleteBorrowerGridRow[]) => {
      const { borrowerRecordId, borrowerLenderDatabaseId } =
        getBorrowerIdentifiersForDelete(rows);

      await typedApiClient.v1.borrower1003ValidationDeleteBorrowerSummary({
        obfuscatedLoanIdentifier,
        borrowerToDelete: {
          borrowerLenderDatabaseId,
          borrowerRecordId,
        },
      });
    },
    onSuccess: () => {
      useBorrower1003Store.setState({ selectedBorrowerId: null });

      queryClient.invalidateQueries({
        queryKey: borrower1003QueryKeys.borrowerData(obfuscatedLoanIdentifier),
      });

      dispatch(setLoanSummaryUpsertOrDelete(true));

      showSnackbar({
        message: 'Borrower Deleted',
        severity: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Borrower was not able to be deleted.',
        severity: 'error',
      });
    },
  });
}
